/**
 * LLM Service
 * Multi-provider LLM communication with streaming support
 */

import { config } from '@/config'
import type { LLMConfig } from '@/types/llm.types'
import type {
    ChatCompletionRequest,
    ChatCompletionResponse,
    StreamChunk
} from '@/types/chat.types'

import { ensureTokenFresh } from '@/lib/supabase'

// API key storage key for sessionStorage
const API_KEY_STORAGE_PREFIX = 'mondai_llm_key_'

/**
 * Get active auth token for API proxy requests (Supabase user JWT fallback to dataApi token)
 */
async function getProxyToken(): Promise<string> {
    const userToken = await ensureTokenFresh()
    return userToken || config.dataApi.token || ''
}

/**
 * Set API key for a provider (stored in sessionStorage - persists across refresh but cleared on tab close)
 */
export function setApiKey(provider: string, apiKey: string): void {
    try {
        sessionStorage.setItem(`${API_KEY_STORAGE_PREFIX}${provider}`, apiKey)
    } catch {
        // Fallback: if sessionStorage is not available, we just won't persist
        console.warn('sessionStorage not available, API key will not persist')
    }
}

/**
 * Get API key for a provider
 */
export function getApiKey(provider: string): string | undefined {
    try {
        return sessionStorage.getItem(`${API_KEY_STORAGE_PREFIX}${provider}`) || undefined
    } catch {
        return undefined
    }
}

/**
 * Clear API key for a provider
 */
export function clearApiKey(provider: string): void {
    try {
        sessionStorage.removeItem(`${API_KEY_STORAGE_PREFIX}${provider}`)
    } catch {
        // Ignore
    }
}

/**
 * Get the endpoint URL based on LLM mode
 */
export function getEndpoint(llmConfig: LLMConfig): string {
    switch (llmConfig.mode) {
        case 'community':
            return config.llm.communityEndpoint
        case 'personal':
        case 'local':
            return llmConfig.endpoint || ''
        default:
            return config.llm.communityEndpoint
    }
}

/**
 * Validate API connection
 */
export async function validateConnection(
    endpoint: string,
    apiKey?: string
): Promise<{ valid: boolean; error?: string }> {
    try {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        }

        if (apiKey) {
            headers['Authorization'] = `Bearer ${apiKey}`
        }

        const response = await fetch(`${endpoint}/models`, {
            method: 'GET',
            headers,
        })

        if (response.ok) {
            return { valid: true }
        }

        return {
            valid: false,
            error: `HTTP ${response.status}: ${response.statusText}`
        }
    } catch (error) {
        return {
            valid: false,
            error: error instanceof Error ? error.message : 'Connection failed'
        }
    }
}

/**
 * Get available models from endpoint
 */
export async function getModels(
    endpoint: string,
    apiKey?: string
): Promise<string[]> {
    try {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        }

        if (apiKey) {
            headers['Authorization'] = `Bearer ${apiKey}`
        }

        const response = await fetch(`${endpoint}/models`, {
            method: 'GET',
            headers,
        })

        if (!response.ok) {
            return []
        }

        const data = await response.json()
        return (data.data || []).map((m: { id: string }) => m.id)
    } catch {
        return []
    }
}

/**
 * Send chat completion request (non-streaming)
 */
export async function sendChatCompletion(
    request: ChatCompletionRequest,
    llmConfig: LLMConfig,
    apiKey?: string
): Promise<ChatCompletionResponse> {
    // Proxy community and personal modes through cm-data-api
    if (llmConfig.mode === 'community' || llmConfig.mode === 'personal') {
        const proxyUrl = config.llm.proxyUrl
        const proxyToken = await getProxyToken()

        if (!proxyUrl) {
            throw new Error('LLM proxy URL not configured')
        }

        const body: any = {
            provider: llmConfig.provider || config.llm.communityProvider || 'openai',
            model: llmConfig.model || config.llm.communityModel || 'gpt-4o-mini',
            messages: request.messages,
            temperature: request.temperature ?? 0.7,
            max_tokens: request.maxTokens || llmConfig.maxTokens || 2048,
            stream: false,
        }

        if (llmConfig.mode === 'personal') {
            body.credentials = {
                apiKey,
                baseUrl: llmConfig.endpoint
            }
        }

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'Authorization': proxyToken ? `Bearer ${proxyToken}` : '',
        }
        if (llmConfig.manifestTier) {
            headers['x-manifest-tier'] = llmConfig.manifestTier
        }

        const response = await fetch(proxyUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
        })

        if (!response.ok) {
            const errorText = await response.text()
            throw new Error(`Proxy request failed: ${response.status} - ${errorText}`)
        }

        const data = await response.json()
        const result = data.result || data

        const content = result.response
            || result.content
            || result.choices?.[0]?.message?.content
            || result.result?.response
            || result.result?.content
            || result.result?.choices?.[0]?.message?.content

        if (!content) {
            throw new Error('Invalid response format from proxy')
        }

        return {
            id: result.id || Date.now().toString(),
            choices: [{
                message: {
                    role: 'assistant',
                    content: content,
                },
                finishReason: 'stop',
            }],
            usage: result.usage ? {
                promptTokens: result.usage.prompt_tokens || 0,
                completionTokens: result.usage.completion_tokens || 0,
                totalTokens: result.usage.total_tokens || 0,
            } : undefined,
        }
    }

    // Local mode: use direct OpenAI-compatible endpoint
    const endpoint = getEndpoint(llmConfig)

    if (!endpoint) {
        throw new Error('No LLM endpoint configured')
    }

    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
    }

    if (apiKey) {
        headers['Authorization'] = `Bearer ${apiKey}`
    }

    const body = {
        model: request.model || llmConfig.model || 'gpt-4o-mini',
        messages: request.messages,
        temperature: request.temperature ?? 0.7,
        max_tokens: request.maxTokens || llmConfig.maxTokens || 2048,
        stream: false,
    }

    const response = await fetch(`${endpoint}/chat/completions`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
    })

    if (!response.ok) {
        let errorMessage = `LLM request failed: ${response.status}`
        try {
            const errorData = await response.json()
            errorMessage = errorData.error?.message || errorData.message || errorMessage
        } catch {
            const errorText = await response.text()
            errorMessage = errorText || errorMessage
        }

        if (response.status === 429) {
            throw new Error('Quota exceeded: You have reached your LLM provider limit. Please check your billing/usage or try again later.')
        }
        throw new Error(errorMessage)
    }

    const data = await response.json()

    return {
        id: data.id,
        choices: data.choices.map((c: { message: { role: string; content: string }; finish_reason: string }) => ({
            message: {
                role: c.message.role as 'assistant',
                content: c.message.content,
            },
            finishReason: c.finish_reason,
        })),
        usage: data.usage ? {
            promptTokens: data.usage.prompt_tokens,
            completionTokens: data.usage.completion_tokens,
            totalTokens: data.usage.total_tokens,
        } : undefined,
    }
}

/**
 * Send chat completion request with streaming
 * Community and Personal modes use cm-data-api proxy
 * Local mode uses direct OpenAI-compatible streaming
 */
export async function* streamChatCompletion(
    request: ChatCompletionRequest,
    llmConfig: LLMConfig,
    apiKey?: string
): AsyncGenerator<StreamChunk> {
    // Community and Personal modes: use API proxy
    if (llmConfig.mode === 'community' || llmConfig.mode === 'personal') {
        const proxyUrl = config.llm.proxyUrl
        const proxyToken = await getProxyToken()

        if (!proxyUrl) {
            yield { type: 'error', error: 'LLM proxy URL not configured.' }
            return
        }

        try {
            const provider = llmConfig.provider || config.llm.communityProvider || 'openai'
            const model = llmConfig.model || config.llm.communityModel || 'gpt-4o-mini'

            const body: any = {
                provider,
                model,
                messages: request.messages,
                temperature: request.temperature ?? 0.7,
                max_tokens: request.maxTokens || llmConfig.maxTokens || 2048,
                stream: provider !== 'manifestnetwork', // Manifest doesn't support streaming
            }

            if (llmConfig.mode === 'personal') {
                body.credentials = {
                    apiKey,
                    baseUrl: llmConfig.endpoint
                }
            }

            if (body.stream) {
                // OpenAI version 2024-05-13+ requires this for usage in streams
                body.stream_options = { include_usage: true }
            }

            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                'Authorization': proxyToken ? `Bearer ${proxyToken}` : '',
            }
            if (llmConfig.manifestTier) {
                headers['x-manifest-tier'] = llmConfig.manifestTier
            }

            const response = await fetch(proxyUrl, {
                method: 'POST',
                headers,
                body: JSON.stringify(body),
            })

            if (!response.ok) {
                const errorText = await response.text()
                yield { type: 'error', error: `Proxy request failed: ${response.status} - ${errorText}` }
                return
            }

            const contentType = response.headers.get('Content-Type')
            const isJson = contentType?.includes('application/json')

            // Handle Non-streaming or JSON fallback
            if (provider === 'manifestnetwork' || !body.stream || isJson) {
                const data = await response.json()
                console.log('[LLM Proxy] Received JSON response:', data)

                const content = data.response
                    || data.content
                    || data.choices?.[0]?.message?.content
                    || data.result?.response
                    || data.result?.content
                    || data.result?.choices?.[0]?.message?.content

                if (content) {
                    yield { type: 'content', content }
                    const tokens = data.tokens_used
                        || data.usage?.total_tokens
                        || data.result?.tokens_used
                        || data.result?.usage?.total_tokens

                    if (tokens) {
                        yield { type: 'usage', usage: { totalTokens: tokens } }
                    }
                    yield { type: 'done' }
                } else {
                    console.error('[LLM Proxy] Invalid JSON response shape:', data)
                    const errorMsg = data.error
                        || data.detail
                        || data.result?.error
                        || data.result?.detail
                        || 'Invalid JSON response shape from proxy';
                    yield { type: 'error', error: errorMsg }
                }
                return
            }

            // Handle other providers (Streaming)
            const reader = response.body?.getReader()
            if (!reader) {
                // Fallback to non-streaming if no reader (some proxies might not stream)
                const data = await response.json()
                const result = data.result || data
                // Try to handle both standard OpenAI format and the proxy's "ok/result" format
                const content = result.choices?.[0]?.message?.content || result.result?.choices?.[0]?.message?.content
                if (content) {
                    yield { type: 'content', content }
                    yield { type: 'done' }
                } else {
                    yield { type: 'error', error: 'No content in response' }
                }
                return
            }

            const decoder = new TextDecoder()
            let buffer = ''

            while (true) {
                const { done, value } = await reader.read()
                if (done) {
                    yield { type: 'done' }
                    break
                }

                buffer += decoder.decode(value, { stream: true })
                const lines = buffer.split('\n')
                buffer = lines.pop() || ''

                for (const line of lines) {
                    const trimmed = line.trim()
                    if (!trimmed || !trimmed.startsWith('data: ')) continue

                    const data = trimmed.slice(6)
                    if (data === '[DONE]') {
                        yield { type: 'done' }
                        return
                    }

                    try {
                        const parsed = JSON.parse(data)
                        const content = parsed.choices?.[0]?.delta?.content
                        if (content) {
                            yield { type: 'content', content }
                        }

                        // Check for usage info in the final chunk
                        const usage = parsed.usage || parsed.result?.usage
                        if (usage) {
                            const totalTokens = usage.total_tokens || usage.totalTokens
                            if (totalTokens) {
                                yield { type: 'usage', usage: { totalTokens } }
                            }
                        }
                    } catch {
                        // Skip invalid JSON
                    }
                }
            }
        } catch (error) {
            yield {
                type: 'error',
                error: error instanceof Error ? error.message : 'Proxy request failed'
            }
        }
        return
    }

    // Local mode: use direct OpenAI-compatible streaming
    const endpoint = getEndpoint(llmConfig)

    if (!endpoint) {
        yield { type: 'error', error: 'No LLM endpoint configured' }
        return
    }

    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
    }

    if (apiKey) {
        headers['Authorization'] = `Bearer ${apiKey}`
    }

    const body = {
        model: request.model || llmConfig.model || 'gpt-4o-mini',
        messages: request.messages,
        temperature: request.temperature ?? 0.7,
        max_tokens: request.maxTokens || llmConfig.maxTokens || 2048,
        stream: true,
    }

    try {
        const response = await fetch(`${endpoint}/chat/completions`, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
        })

        if (!response.ok) {
            let errorMessage = `LLM request failed: ${response.status}`
            try {
                const errorData = await response.json()
                errorMessage = errorData.error?.message || errorData.message || errorMessage
            } catch {
                const errorText = await response.text()
                errorMessage = errorText || errorMessage
            }

            if (response.status === 429) {
                yield { type: 'error', error: 'Quota exceeded: You have reached your LLM provider limit. Please check your usage or try again later.' }
            } else {
                yield { type: 'error', error: errorMessage }
            }
            return
        }

        const reader = response.body?.getReader()
        if (!reader) {
            yield { type: 'error', error: 'No response body' }
            return
        }

        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
            const { done, value } = await reader.read()

            if (done) {
                yield { type: 'done' }
                break
            }

            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() || ''

            for (const line of lines) {
                const trimmed = line.trim()
                if (!trimmed || !trimmed.startsWith('data: ')) continue

                const data = trimmed.slice(6)
                if (data === '[DONE]') {
                    yield { type: 'done' }
                    return
                }

                try {
                    const parsed = JSON.parse(data)
                    const content = parsed.choices?.[0]?.delta?.content
                    if (content) {
                        yield { type: 'content', content }
                    }
                } catch {
                    // Skip invalid JSON
                }
            }
        }
    } catch (error) {
        yield {
            type: 'error',
            error: error instanceof Error ? error.message : 'Stream failed'
        }
    }
}

/**
 * Estimate token count (rough approximation)
 * ~4 characters per token for English text
 */
export function estimateTokens(text: string): number {
    return Math.ceil(text.length / 4)
}

/**
 * Check if within daily token limit
 * Only enforces limits for 'community' mode.
 * Personal/Local modes are exempt.
 */
export function checkTokenLimit(
    usedTokens: number,
    additionalTokens: number,
    mode: string = 'community',
    profileLimit?: number | null,
    globalLimit?: number | null
): boolean {
    // Limits only apply to community mode
    if (mode !== 'community') return true

    // Precedence: Profile-specific override > Global admin setting > .env fallback
    const resolvedLimit = (profileLimit !== undefined && profileLimit !== null)
        ? profileLimit
        : (globalLimit !== undefined && globalLimit !== null)
            ? globalLimit
            : config.llm.dailyTokenLimit

    return (usedTokens + additionalTokens) <= resolvedLimit
}

/**
 * Get available LLM providers from proxy
 */
export async function fetchCommunityProviders(): Promise<any[]> {
    const proxyUrl = config.llm.proxyUrl
    const proxyToken = await getProxyToken()

    try {
        console.log(`[LLM Service] Fetching providers from: ${proxyUrl}/providers?scope=llm`)
        const response = await fetch(`${proxyUrl}/providers?scope=llm`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': proxyToken ? `Bearer ${proxyToken}` : '',
            },
        })

        if (!response.ok) {
            const errorText = await response.text()
            console.warn(`[LLM Service] Failed to fetch providers: ${response.status} - ${errorText}`)
            return []
        }

        const data = await response.json()
        console.log(`[LLM Service] Found ${data.providers?.length || 0} providers:`, data.providers)
        return data.providers || []
    } catch (e) {
        console.error('[LLM Service] Provider fetch failed:', e)
        return []
    }
}

/**
 * Get available models for a specific provider
 */
export async function fetchProviderModels(provider: string, apiKey?: string, endpoint?: string): Promise<string[]> {
    const proxyUrl = config.llm.proxyUrl
    const proxyToken = await getProxyToken()

    if (!proxyUrl) return []

    try {
        console.log(`[LLM Service] Fetching models for provider: ${provider}`)

        // If the provider is custom, the backend switch case doesn't support 'custom',
        // so we map 'custom' to 'openai' for the backend provider parameter,
        // but pass the custom endpoint in credentials.
        const resolvedProvider = provider === 'custom' ? 'openai' : provider;

        // Use URL with query parameters as suggested by Swagger/usage
        const url = new URL(proxyUrl, window.location.origin);
        url.searchParams.append('provider', resolvedProvider);
        url.searchParams.append('callType', 'list.models');

        const response = await fetch(url.toString(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': proxyToken ? `Bearer ${proxyToken}` : '',
            },
            body: JSON.stringify({
                // Provide a dummy message to satisfy the backend's min-length-1 validation
                messages: [{ role: 'user', content: 'list.models' }],
                credentials: apiKey ? {
                    apiKey,
                    baseUrl: endpoint || undefined
                } : undefined
            }),
        })

        if (!response.ok) {
            const errorText = await response.text()
            console.warn(`[LLM Service] Failed to fetch models: ${response.status} - ${errorText}`)
            return []
        }

        const data = await response.json()
        console.log('[LLM Service] Raw model data received:', data)

        // Extract models array from various possible wrapper keys
        const rawModels = data.models || data.result?.data || data.data?.models || data.results || data.data || []

        // Normalize: Map objects to IDs, or keep strings as-is
        let models = Array.isArray(rawModels)
            ? rawModels.map((m: any) => {
                if (typeof m === 'string') return m;
                return m.id || m.model || m.modelKey || m.name || String(m);
            })
            : [];

        // Filter models to exclude research, embedding, reasoning, or images
        // and focus on current popular models supporting tools. Version must be >= 4.
        if (provider === 'openai' || provider === 'anthropic' || provider === 'custom') {
            models = models.filter((modelId: string) => {
                const id = modelId.toLowerCase();
                
                // Exclude embedding models
                if (id.includes('embed') || id.includes('similarity')) return false;
                
                // Exclude image generation models
                if (id.includes('dall') || id.includes('dalle') || id.includes('image')) return false;
                
                // Exclude audio / translation / voice models
                if (id.includes('whisper') || id.includes('tts') || id.includes('audio') || id.includes('realtime') || id.includes('voice')) return false;
                
                // Exclude moderation models
                if (id.includes('moderation')) return false;

                // Exclude reasoning models focusing on research/reasoning (like o1/o3/reasoning/research)
                if (id.startsWith('o1') || id.startsWith('o3') || id.includes('reasoning') || id.includes('research') || id.includes('-preview')) return false;

                // Exclude instruct-only models (not suitable for chat assistant)
                if (id.includes('instruct')) return false;

                // Filter by version (must be at least 4 for gpt and claude)
                if (id.includes('gpt')) {
                    const gptMatch = id.match(/gpt-?(\d+)/);
                    if (gptMatch) {
                        const version = parseInt(gptMatch[1], 10);
                        if (version < 4) return false;
                    } else {
                        return false;
                    }
                } else if (id.includes('claude')) {
                    const claudeMatch = id.match(/claude-?(\d+)/);
                    let version = 0;
                    if (claudeMatch) {
                        version = parseInt(claudeMatch[1], 10);
                    } else {
                        const digitMatch = id.match(/(\d+)/);
                        if (digitMatch) {
                            version = parseInt(digitMatch[1], 10);
                        }
                    }
                    if (version < 4) return false;
                }
                
                return true;
            });
        }

        console.log(`[LLM Service] Resolved and filtered ${models.length} models for ${provider}:`, models)
        return models
    } catch (e) {
        console.error(`[LLM Service] Model fetch failed for ${provider}:`, e)
        return []
    }
}

/**
 * Test a community LLM configuration (Sync)
 */
export async function testCommunityLlm(
    provider: string,
    model: string
): Promise<{ success: boolean; response: string; tokens?: number; error?: string }> {
    const proxyUrl = config.llm.proxyUrl
    const proxyToken = await getProxyToken()

    if (!proxyUrl) {
        return { success: false, response: '', error: 'Community LLM proxy URL not configured.' }
    }

    try {
        console.log(`[LLM Service] Testing connection: ${provider} / ${model} via ${proxyUrl}`)
        const response = await fetch(proxyUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': proxyToken ? `Bearer ${proxyToken}` : '',
            },
            body: JSON.stringify({
                provider,
                model,
                messages: [{ role: 'user', content: 'Say "Connection successful!" short response.' }],
                temperature: 1,
                max_completion_tokens: 50,
                stream: false,
            }),
        })

        if (!response.ok) {
            const errorText = await response.text()
            console.warn(`[LLM Service] Test failed: ${response.status} - ${errorText}`)
            return { success: false, response: '', error: `HTTP ${response.status}: ${errorText}` }
        }

        const data = await response.json()
        const content = data.response
            || data.content
            || data.choices?.[0]?.message?.content
            || data.result?.response
            || data.result?.content
            || data.result?.choices?.[0]?.message?.content

        if (content) {
            console.log('[LLM Service] Test successful')
            const tokens = data.tokens_used
                || data.usage?.total_tokens
                || data.result?.tokens_used
                || data.result?.usage?.total_tokens

            return { success: true, response: content, tokens }
        } else {
            console.error('[LLM Service] Invalid test response shape:', data)
            return { success: false, response: '', error: 'Invalid response format from proxy.' }
        }
    } catch (error) {
        console.error('[LLM Service] Test request failed:', error)
        return {
            success: false,
            response: '',
            error: error instanceof Error ? error.message : 'Test request failed'
        }
    }
}
